import { Directive, ElementRef, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';

@Directive({
  selector: '[bnIsElementStuck]',
  host: { class: 'bn-sticky-save-button d-flex justify-content-between' },
})
export class IsElementStuckDirective implements OnInit, OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setupIntersectionObserver();
    if (this.isStuckEnabled && this.isCurrentlyStuck) {
      this.syncPlaceholderDimensions();
    }
  }

  private isStuckEnabled = false;
  private scrollContainer: HTMLElement | null = null;
  private isCurrentlyStuck = false;
  private resizeObserver: ResizeObserver | null = null;
  private placeholderElement: HTMLDivElement | null = null;
  private sentinelElement: HTMLDivElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private placeholderHeight = 0;

  @Input()
  public set stickyEnabled(val: boolean) {
    this.isStuckEnabled = val;
    if (val) {
      this.setupIntersectionObserver();
      this.evaluateStickiness();
    } else {
      this.setUnstuck();
    }
  }

  ngOnInit(): void {
    this.placeholderElement = document.createElement('div');
    this.placeholderElement.style.display = 'none';
    this.element.nativeElement.insertAdjacentElement('afterend', this.placeholderElement);

    this.sentinelElement = document.createElement('div');
    this.sentinelElement.style.display = 'block';
    this.sentinelElement.style.height = '1px';
    this.sentinelElement.style.margin = '0';
    this.sentinelElement.style.pointerEvents = 'none';
    this.placeholderElement.insertAdjacentElement('afterend', this.sentinelElement);

    this.resizeObserver = new ResizeObserver(() => {
      this.setupIntersectionObserver();
      if (this.isStuckEnabled) {
        this.evaluateStickiness();
      }
    });
    this.resizeObserver.observe(document.body, {});
    this.resizeObserver.observe(this.element.nativeElement);

    this.scrollContainer = this.findOverlayScrollbarsViewport();
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.placeholderElement?.remove();
    this.sentinelElement?.remove();
  }

  private findOverlayScrollbarsViewport(): HTMLElement | null {
    let parent = this.element.nativeElement.parentElement;

    while (parent) {
      if (parent.hasAttribute('data-overlayscrollbars-viewport')) {
        const computedStyle = window.getComputedStyle(parent);
        // Make sure it's not hidden with display: contents (mobile)
        if (computedStyle.display !== 'contents') {
          return parent;
        }
      }
      parent = parent.parentElement;
    }

    return null;
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver?.disconnect();

    if (!this.sentinelElement) return;

    const bottomOffset = this.getBottomOffset();

    this.intersectionObserver = new IntersectionObserver(() => this.evaluateStickiness(), {
      root: this.scrollContainer,
      threshold: 0,
      rootMargin: `0px 0px -${bottomOffset}px 0px`,
    });

    this.intersectionObserver.observe(this.sentinelElement);
  }

  private evaluateStickiness(): void {
    if (!this.isStuckEnabled) {
      this.setUnstuck();
      return;
    }

    const bottomOffset = this.getBottomOffset();
    const viewportBottom = this.scrollContainer ? this.scrollContainer.getBoundingClientRect().bottom : window.innerHeight;
    const sentinelTop = this.sentinelElement?.getBoundingClientRect().top ?? this.element.nativeElement.getBoundingClientRect().bottom;
    const shouldStick = sentinelTop > viewportBottom - bottomOffset;

    if (shouldStick && !this.isCurrentlyStuck) {
      this.setStuck();
    } else if (!shouldStick && this.isCurrentlyStuck) {
      this.setUnstuck();
    } else if (shouldStick) {
      this.updateStuckPositioning();
    }
  }

  private getBottomOffset(): number {
    if (window.innerWidth >= 992) return 0;

    // Match the CSS clearance for a fixed bottom toolbar: the dashboard sets
    // --bn-mobile-menu-height to 0 (its toolbar was removed), the partner portal
    // does not set it and keeps the legacy 70px.
    const cssValue = window.getComputedStyle(document.documentElement).getPropertyValue('--bn-mobile-menu-height');
    const parsed = Number.parseFloat(cssValue);

    return Number.isNaN(parsed) ? 70 : parsed;
  }

  private updateStuckPositioning(): void {
    this.syncPlaceholderDimensions();
    this.syncStuckElementPosition();
  }

  private syncPlaceholderDimensions(): void {
    if (!this.placeholderElement) return;

    const styles = window.getComputedStyle(this.element.nativeElement);
    const height = this.isCurrentlyStuck && this.placeholderHeight ? this.placeholderHeight : this.element.nativeElement.offsetHeight;

    this.placeholderElement.style.display = 'block';
    this.placeholderElement.style.height = `${height}px`;
    this.placeholderElement.style.marginTop = styles.marginTop;
    this.placeholderElement.style.marginBottom = styles.marginBottom;
  }

  private syncStuckElementPosition(): void {
    if (!this.isCurrentlyStuck || !this.placeholderElement) return;

    const rect = this.placeholderElement.getBoundingClientRect();
    this.element.nativeElement.style.width = `${rect.width}px`;
    this.element.nativeElement.style.left = `${rect.left}px`;
  }

  public setStuck(): void {
    this.placeholderHeight = this.element.nativeElement.offsetHeight;
    this.syncPlaceholderDimensions();
    this.isCurrentlyStuck = true;
    this.element.nativeElement.setAttribute('stuck', '');
    this.syncStuckElementPosition();
  }

  public setUnstuck(): void {
    this.isCurrentlyStuck = false;
    this.placeholderHeight = 0;
    this.element.nativeElement.removeAttribute('stuck');
    this.element.nativeElement.style.width = '';
    this.element.nativeElement.style.left = '';

    if (this.placeholderElement) {
      this.placeholderElement.style.display = 'none';
      this.placeholderElement.style.height = '';
      this.placeholderElement.style.marginTop = '';
      this.placeholderElement.style.marginBottom = '';
    }
  }
}
